import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { LOGO_URL } from '../mock/mock';

import { API } from '../config';

const SettingsContext = createContext(null);

const DEFAULTS = {
  general: {
    site_name: 'TrainPanda',
    tagline: "India's trusted train travel companion",
    logo_url: LOGO_URL,
    favicon_url: '',
    support_email: 'help@trainpanda.com',
    support_phone: '1800-123-4567',
  },
  seo: {
    meta_title: 'TrainPanda - Check PNR - Live Train Status - Order Food in Train',
    meta_description: 'TrainPanda helps you track live train status, check PNR status, and order delicious food directly to your seat for a smooth and stress-free journey.',
    meta_keywords: 'train status, pnr status, food on train, train tracking',
    og_image: '',
    google_analytics_id: '',
  },
  payment: { cod_enabled: true, enabled: false, mode: 'test', razorpay_key_id: '' },
  modules: { live_status: true, pnr_status: true, food_on_train: true, stories: true, cab_booking: false },
  mobile: {},
  code_injection: { head_code: '', body_code: '' },
  feature_notes: { pnr_status: '', live_status: '', cabs: '', schedule: '', food_on_train: '', stories: '' },
  myf_cabs: { advance_enabled: false, advance_type: 'fixed', advance_value: 100 },
};

// Injects raw HTML/JS (analytics, GTM, etc.) so that <script> tags actually execute.
const injectCode = (html, target, marker) => {
  try {
    document.querySelectorAll(`[data-tp-inject="${marker}"]`).forEach(n => n.remove());
    if (!html || !html.trim() || !target) return;
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    Array.from(tpl.content.childNodes).forEach(node => {
      let el;
      if (node.tagName === 'SCRIPT') {
        el = document.createElement('script');
        Array.from(node.attributes).forEach(a => el.setAttribute(a.name, a.value));
        el.text = node.textContent;
      } else {
        el = node.cloneNode(true);
      }
      if (el.setAttribute) el.setAttribute('data-tp-inject', marker);
      target.appendChild(el);
    });
  } catch (_) { /* ignore malformed code */ }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);

  useEffect(() => {
    if (!document.title || document.title.includes('{{')) {
      document.title = DEFAULTS.seo.meta_title || 'TrainPanda - Check PNR - Live Train Status - Order Food in Train';
    }
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/public/settings`);
        if (!alive) return;
        const merged = {
          general: { ...DEFAULTS.general, ...(r.data.general || {}) },
          seo: { ...DEFAULTS.seo, ...(r.data.seo || {}) },
          payment: { ...DEFAULTS.payment, ...(r.data.payment || {}) },
          modules: { ...DEFAULTS.modules, ...(r.data.modules || {}) },
          mobile: { ...DEFAULTS.mobile, ...(r.data.mobile || {}) },
          code_injection: { ...DEFAULTS.code_injection, ...(r.data.code_injection || {}) },
          feature_notes: { ...DEFAULTS.feature_notes, ...(r.data.feature_notes || {}) },
          myf_cabs: { ...DEFAULTS.myf_cabs, ...(r.data.myf_cabs || {}) },
          _loaded: true,
        };
        if (!merged.general.logo_url) merged.general.logo_url = LOGO_URL;
        setSettings(merged);
        // Inject admin-configured custom code (Google Analytics / GTM / etc.) site-wide
        injectCode(merged.code_injection.head_code, document.head, 'head');
        injectCode(merged.code_injection.body_code, document.body, 'body');
        // Apply dynamic document title and favicon
        if (merged.seo.meta_title) document.title = merged.seo.meta_title;
        const activeFavicon = merged.general.favicon_url || '/favicon.png?v=6';
        if (activeFavicon) {
          const links = document.querySelectorAll("link[rel*='icon'], link[rel='apple-touch-icon']");
          if (links.length > 0) {
            links.forEach((l) => { l.href = activeFavicon; });
          } else {
            const link = document.createElement('link');
            link.rel = 'icon';
            link.href = activeFavicon;
            document.head.appendChild(link);
          }
        }
        // Meta description
        if (merged.seo.meta_description) {
          let meta = document.querySelector("meta[name='description']");
          if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'description';
            document.head.appendChild(meta);
          }
          meta.content = merged.seo.meta_description;
        }
      } catch (_) { setSettings(prev => ({ ...prev, _loaded: true })); }
    };
    load();
    return () => { alive = false; };
  }, []);

  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => useContext(SettingsContext) || DEFAULTS;
